import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Form from "react-bootstrap/Form";
import ReactSelect from "react-select";
import {
  Modal,
  Input,
  Select,
  Textarea,
  Button,
} from "../../../components/ui";
import BootstrapSpinner from "../../../sharedComponent/BootstrapSpinner";
import AnnouncementCard from "./AnnouncementCard";

const today = () => new Date().toISOString().split("T")[0];

const AnnouncementFormModal = ({
  mode,
  show,
  onHide,
  form,
  updateField,
  validated,
  selectedBranches,
  setSelectedBranches,
  reset,
  branchName,
  teamName,
  employName,
  priority,
  onSubmit,
  btnLoading,
  announcements,
  loading,
  onConfirmDelete,
}) => {
  const previewUrlRef = useRef(null);

  useEffect(() => {
    const file = form && form.AttachmentPath;
    if (!(file instanceof File)) return undefined;

    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;

    return () => {
      URL.revokeObjectURL(url);
      if (previewUrlRef.current === url) previewUrlRef.current = null;
    };
  }, [form && form.AttachmentPath]);

  if (mode === "view") {
    return (
      <Modal show={show} onHide={onHide} size="xl" title="Announcement">
        {loading ? (
          <div className="ann-modal-loader">
            <BootstrapSpinner />
          </div>
        ) : announcements && announcements.length > 0 ? (
          announcements.map((anou, index) => (
            <AnnouncementCard key={"anou" + index} announcement={anou} />
          ))
        ) : (
          <div className="ann-modal-empty">Data Not Found</div>
        )}
      </Modal>
    );
  }

  if (mode === "delete") {
    return (
      <Modal
        show={show}
        onHide={onHide}
        size="md"
        title="Delete Announcement"
        footer={
          <>
            <Button variant="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button variant="danger" onClick={onConfirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        Are you sure you want to delete this announcement?
      </Modal>
    );
  }

  // create / edit
  const branchOptions = branchName.map((b) => ({
    label: b.LocationName,
    value: b.LocationId,
  }));

  const handleClose = () => {
    if (typeof reset === "function") reset();
    onHide();
  };

  const submitLabel = mode === "edit" ? "Update" : "Add";

  const titleError =
    validated && !form.Title ? "Please Enter Title" : "";
  const dateError =
    validated && !form.AnnouncementDate
      ? "Please select Announcemnet Date"
      : "";
  const priorityError =
    validated && !form.PriorityId ? "Please select priority" : "";
  const messageError =
    validated && !form.Message ? "Please Enter Message or Description" : "";

  const dateValue =
    form.AnnouncementDate && form.AnnouncementDate.length
      ? new Date(form.AnnouncementDate).toISOString().slice(0, 10)
      : "";

  const attachedFileName =
    form.AttachmentPath instanceof File ? form.AttachmentPath.name : "";

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      title={mode === "edit" ? "Edit Announcement" : "Create Announcement"}
    >
      <Form
        noValidate
        validated={validated}
        onSubmit={onSubmit}
        className="ann-form"
      >
        <div className="ann-form__grid">
          <div className="ann-form__field">
            <Input
              label="Title"
              type="text"
              value={form.Title}
              onChange={(e) => updateField("Title", e.target.value)}
              required
              error={titleError}
            />
          </div>

          <div className="ann-form__field">
            <Input
              label="Date"
              type="date"
              value={dateValue}
              onChange={(e) => updateField("AnnouncementDate", e.target.value)}
              min={today()}
              required
              error={dateError}
            />
          </div>

          <div className="ann-form__field">
            <label className="ann-form__label">Assign To Branch</label>
            <div className="ann-form__multiselect">
              <ReactSelect
                options={branchOptions}
                value={selectedBranches}
                onChange={setSelectedBranches}
                isMulti
              />
            </div>
          </div>

          <div className="ann-form__field">
            <Select
              label="Priority"
              value={form.PriorityId}
              onChange={(e) => updateField("PriorityId", e.target.value)}
              required
              error={priorityError}
            >
              <option value="">Please select</option>
              {priority &&
                priority.map((pname, index) => (
                  <option key={"priority" + index} value={pname.PriorityId}>
                    {pname.PriorityName}
                  </option>
                ))}
            </Select>
          </div>

          <div className="ann-form__field">
            <Select
              label="Assign To Team"
              value={form.AssignToTeamId}
              onChange={(e) => updateField("AssignToTeamId", e.target.value)}
            >
              <option value="">Please select</option>
              {teamName &&
                teamName.map((team, index) => (
                  <option key={"teadd" + index} value={team.TeamId}>
                    {team.TeamName}
                  </option>
                ))}
            </Select>
          </div>

          <div className="ann-form__field">
            <Select
              label="Assign To Members"
              value={form.AssignToEmployeeId}
              onChange={(e) =>
                updateField("AssignToEmployeeId", e.target.value)
              }
            >
              <option value="">Please select</option>
              {employName &&
                employName.map((name, index) => (
                  <option key={"dfgve" + index} value={name.Id}>
                    {name.FullName}
                  </option>
                ))}
            </Select>
          </div>

          <div className="ann-form__field ann-form__field--full">
            <label className="ann-form__label">Attachment</label>
            <div className="ann-form__file-zone">
              <input
                className="ann-form__file-input"
                type="file"
                accept="image/png, image/gif, image/jpeg"
                onChange={(e) =>
                  updateField("AttachmentPath", e.target.files[0])
                }
              />
              <p className="ann-form__file-text ann-form__file-text--strong">
                {attachedFileName ||
                  "Click or drop a PNG, GIF, or JPEG image here"}
              </p>
              {!attachedFileName && (
                <p className="ann-form__file-text">
                  Maximum one image per announcement.
                </p>
              )}
            </div>
          </div>

          <div className="ann-form__field">
            <Textarea
              label="Attachment Preview"
              cols="25"
              rows="6"
              value={form.AttachmentPreview}
              onChange={(e) =>
                updateField("AttachmentPreview", e.target.value)
              }
            />
          </div>

          <div className="ann-form__field">
            <Textarea
              label="Message/Description"
              cols="30"
              rows="6"
              value={form.Message}
              onChange={(e) => updateField("Message", e.target.value)}
              required
              error={messageError}
            />
          </div>
        </div>

        <div className="ann-form__actions">
          <Button variant="secondary" type="button" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={btnLoading}>
            {submitLabel}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

AnnouncementFormModal.propTypes = {
  mode: PropTypes.oneOf(["create", "edit", "view", "delete"]),
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  form: PropTypes.object,
  updateField: PropTypes.func,
  validated: PropTypes.bool,
  selectedBranches: PropTypes.array,
  setSelectedBranches: PropTypes.func,
  reset: PropTypes.func,
  branchName: PropTypes.array,
  teamName: PropTypes.array,
  employName: PropTypes.array,
  priority: PropTypes.array,
  onSubmit: PropTypes.func,
  btnLoading: PropTypes.bool,
  announcements: PropTypes.array,
  loading: PropTypes.bool,
  onConfirmDelete: PropTypes.func,
};

AnnouncementFormModal.defaultProps = {
  mode: "create",
  branchName: [],
  teamName: [],
  employName: [],
  priority: [],
  announcements: [],
  loading: false,
  btnLoading: false,
  validated: false,
  selectedBranches: [],
};

export default AnnouncementFormModal;
